import { useEffect, useRef } from 'react';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

/**
 * "The Solution: Scaffold" section — ports the vanilla HTML scaffold section to React.
 *
 * Layout:
 *   Left:  Product mockup illustration (code editor + Scout AI sidebar)
 *   Right: Features area with md-assist-chip, LEARN stepper (L-E-A-R-N),
 *          8 feature cards, and a Knowledge Garden preview
 *
 * Animations:
 *   - fade-up / slide-left / slide-right elements get `revealed` when the section is visible
 *   - LEARN stepper steps animate sequentially with 300ms stagger
 *   - Knowledge Garden nodes animate sequentially with 200ms stagger
 */
export function ScaffoldSection() {
  const [sectionRef, isVisible] = useIntersectionObserver<HTMLElement>({ threshold: 0.1 });

  /* ── LEARN stepper sequential animation ─────────────────────────── */
  const stepperRef = useRef<HTMLDivElement>(null);
  const hasAnimatedStepper = useRef(false);

  useEffect(() => {
    if (!isVisible || hasAnimatedStepper.current) return;
    hasAnimatedStepper.current = true;
    const steps = stepperRef.current?.querySelectorAll('.learn-step');
    steps?.forEach((step, i) => {
      setTimeout(() => step.classList.add('active'), i * 300);
    });
  }, [isVisible]);

  /* ── Knowledge Garden sequential animation ──────────────────────── */
  const gardenRef = useRef<HTMLDivElement>(null);
  const hasAnimatedGarden = useRef(false);

  useEffect(() => {
    if (!isVisible || hasAnimatedGarden.current) return;
    hasAnimatedGarden.current = true;
    const nodes = gardenRef.current?.querySelectorAll('.garden-node');
    nodes?.forEach((node, i) => {
      setTimeout(() => node.classList.add('visible'), i * 200);
    });
  }, [isVisible]);

  /* ── Helpers ─────────────────────────────────────────────────────── */
  const revealed = isVisible ? 'revealed' : '';

  const stagger = (i: number): React.CSSProperties =>
    ({ '--stagger': i }) as React.CSSProperties;

  return (
    <section
      id="scaffold"
      className="section scaffold-section"
      aria-label="Scaffold: The Solution"
      ref={sectionRef}
    >
      <div className="section-inner">
        <h2 className={`section-title fade-up ${revealed}`}>The Solution: Scaffold</h2>
        <p className={`section-subtitle fade-up ${revealed}`} style={stagger(1)}>
          A coding bootcamp designed to prove that AI can make people smarter, not more dependent.
        </p>

        <div className="scaffold-layout">
          {/* Left: Product Illustration */}
          <div className={`scaffold-illustration slide-left ${revealed}`}>
            <div className="product-mockup">
              <md-elevation />
              <div className="mockup-topbar">
                <div className="mockup-dots">
                  <span className="dot dot-red" />
                  <span className="dot dot-yellow" />
                  <span className="dot dot-green" />
                </div>
                <span className="mockup-title">Scaffold IDE</span>
              </div>
              <div className="mockup-body">
                <div className="mockup-editor">
                  <div className="mockup-line">
                    <span className="code-keyword">function</span>{' '}
                    <span className="code-fn">solve</span>(
                    <span className="code-param">arr</span>) {'{'}
                  </div>
                  <div className="mockup-line">
                    &nbsp;&nbsp;<span className="code-keyword">return</span> arr.
                    <span className="code-fn">filter</span>(
                    <span className="code-param">x</span> ={'>'} x {'>'} 0);
                  </div>
                  <div className="mockup-line">{'}'}</div>
                  <div className="mockup-cursor" />
                </div>
                <div className="mockup-sidebar">
                  <div className="mockup-scout-header">
                    <span className="material-symbols-outlined">smart_toy</span>
                    Scout AI
                  </div>
                  <div className="mockup-scout-msg scout-msg-ai">
                    What does <code>.filter()</code> return when no elements match?
                  </div>
                  <div className="mockup-scout-msg scout-msg-user">An empty array?</div>
                  <div className="mockup-scout-msg scout-msg-ai">
                    Exactly! Now, what happens if <code>arr</code> is undefined?
                  </div>
                  <div className="mockup-sr-bar">
                    <span className="sr-label">Spaced Review</span>
                    <div className="sr-dots">
                      <span className="sr-dot active" />
                      <span className="sr-dot active" />
                      <span className="sr-dot" />
                      <span className="sr-dot" />
                      <span className="sr-dot" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Features */}
          <div className={`scaffold-features slide-right ${revealed}`}>
            <md-assist-chip label="Proof of Concept: Coding Bootcamp" elevated>
              <md-icon slot="icon">science</md-icon>
            </md-assist-chip>

            {/* LEARN Stepper */}
            <div className="learn-stepper" aria-label="LEARN methodology" ref={stepperRef}>
              <div className="learn-step" data-step="L">
                <span className="step-letter">L</span>
                <span className="step-word">Learn</span>
                <span className="step-desc">Concept introduction</span>
              </div>
              <div className="step-connector" />
              <div className="learn-step" data-step="E">
                <span className="step-letter">E</span>
                <span className="step-word">Engage</span>
                <span className="step-desc">Active problem-solving</span>
              </div>
              <div className="step-connector" />
              <div className="learn-step" data-step="A">
                <span className="step-letter">A</span>
                <span className="step-word">Apply</span>
                <span className="step-desc">Real-world projects</span>
              </div>
              <div className="step-connector" />
              <div className="learn-step" data-step="R">
                <span className="step-letter">R</span>
                <span className="step-word">Reinforce</span>
                <span className="step-desc">Spaced repetition</span>
              </div>
              <div className="step-connector" />
              <div className="learn-step" data-step="N">
                <span className="step-letter">N</span>
                <span className="step-word">Navigate</span>
                <span className="step-desc">Adaptive pathways</span>
              </div>
            </div>

            <div className="feature-grid">
              <div className="feature-card">
                <span className="material-symbols-outlined feature-card-icon">smart_toy</span>
                <div>
                  <strong>Scout AI Tutor</strong>
                  <p>Socratic guidance powered by Google Gemini that asks instead of tells</p>
                </div>
              </div>
              <div className="feature-card">
                <span className="material-symbols-outlined feature-card-icon">trending_down</span>
                <div>
                  <strong>Graduated AI Withdrawal</strong>
                  <p>AI support decreases as competence increases, building independence</p>
                </div>
              </div>
              <div className="feature-card">
                <span className="material-symbols-outlined feature-card-icon">query_stats</span>
                <div>
                  <strong>Process-Based Assessment</strong>
                  <p>Measures HOW you think, not just WHAT you answer</p>
                </div>
              </div>
              <div className="feature-card">
                <span className="material-symbols-outlined feature-card-icon">event_repeat</span>
                <div>
                  <strong>Spaced Repetition Engine</strong>
                  <p>Algorithmically-timed reviews that prevent the forgetting curve</p>
                </div>
              </div>
              <div className="feature-card">
                <span className="material-symbols-outlined feature-card-icon">
                  accessibility_new
                </span>
                <div>
                  <strong>Neurodivergent-First Design</strong>
                  <p>3 clicks max, no shame-based patterns, predictable navigation</p>
                </div>
              </div>
              <div className="feature-card">
                <span className="material-symbols-outlined feature-card-icon">group</span>
                <div>
                  <strong>Human Mentors + Peer Learning</strong>
                  <p>1:1 sessions, code reviews, and collaborative problem-solving</p>
                </div>
              </div>
              <div className="feature-card">
                <span className="material-symbols-outlined feature-card-icon">bug_report</span>
                <div>
                  <strong>Reverse Turing Protocol</strong>
                  <p>Debug Scout's flawed reasoning, deeper than getting answers</p>
                </div>
              </div>
              <div className="feature-card">
                <span className="material-symbols-outlined feature-card-icon">lock</span>
                <div>
                  <strong>Hypothesis Lock</strong>
                  <p>
                    Commit a prediction before running code. Prediction-error accelerates learning
                  </p>
                </div>
              </div>
            </div>

            {/* Knowledge Garden Preview */}
            <div className="knowledge-garden-preview" ref={gardenRef}>
              <h4>
                <span className="material-symbols-outlined">yard</span>
                Knowledge Garden
              </h4>
              <p>
                Every concept you learn becomes a living node. Practice it and it blooms gold.
                Neglect it and it decays. Your garden is your real-time map of mastery.
              </p>
              <div className="garden-grid">
                <div className="garden-node garden-gold" data-concept="Closures">
                  <span className="garden-node-icon">local_florist</span>
                  <span className="garden-node-label">Closures</span>
                </div>
                <div className="garden-node garden-silver" data-concept="Promises">
                  <span className="garden-node-icon">eco</span>
                  <span className="garden-node-label">Promises</span>
                </div>
                <div className="garden-node garden-rusted" data-concept="Prototypes">
                  <span className="garden-node-icon">forest</span>
                  <span className="garden-node-label">Prototypes</span>
                </div>
                <div className="garden-node garden-cracked" data-concept="Recursion">
                  <span className="garden-node-icon">nature</span>
                  <span className="garden-node-label">Recursion</span>
                </div>
              </div>
              <div className="garden-legend">
                <span className="legend-item">
                  <span className="legend-dot garden-dot-gold" />
                  Mastered
                </span>
                <span className="legend-item">
                  <span className="legend-dot garden-dot-silver" />
                  Fading
                </span>
                <span className="legend-item">
                  <span className="legend-dot garden-dot-rusted" />
                  At risk
                </span>
                <span className="legend-item">
                  <span className="legend-dot garden-dot-cracked" />
                  Needs review
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
